import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import UITabs from "../../../ui/UITabs";
import EventBasicInfo from "../BasicInfo";
import EventRoles from "../Roles";
import EventCrewSize from "../CrewSize";
import EventZones from "../Zones";
import {
  ZONE_APPLICANTS_PORTALS,
  ZONE_APPLICANTS_URL_PARAMS,
} from "../ZoneApplicants/portal";
import RequirementsComments from "../RequirementsComments";
import JoiningRequestsApplicants from "../Applicants";
import EvaluationRounds from "../Evaluations";
import CompanyEventTimesheetTab from "../../../../containers/Company-portal/Events/View/CompanyEventTimesheetTab";
import CompanyEventReportsTab from "../../../../containers/Company-portal/Events/View/CompanyEventReportsTab";
import CompanyEventClientReportsTab from "../../../../containers/Company-portal/Events/View/CompanyEventClientReportsTab";
import ClientEventClientReportsTab from "../../../../containers/Client-portal/Events/View/ClientEventClientReportsTab";
import CompanyEventPayrollTab from "../../../../containers/Company-portal/Events/View/CompanyEventPayrollTab";
import EventHeatMap from "../HeatMap/EventHeatMap";
import { TIMESHEET_URL_PARAMS } from "../timesheet/constants";
import { PAYROLL_URL_PARAMS } from "../Payroll/GeneratePayroll/constants/payrollUrlParams";
import messages from "../../../../assets/locale/messages";
import {
  EVENT_FORM_STEP_IDS,
  getCompanyEditStep,
} from "../../Form/formStepIds";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  shouldShowEventApplicantsTab,
  shouldShowEventEvaluationsTab,
  shouldShowEventPayrollTab,
} from "../../../../utils/companyPortal/eventEditRules";
import {
  EVENT_REPORT_URL_PARAM,
  EVENT_VIEW_DEFAULT_TAB,
  EVENT_VIEW_TABS,
  getAllowedEventViewTabs,
  isEventPayrollTabEnabled,
  isKnownEventViewTab,
  resolveEventViewTabFromSearch,
} from "../../../../utils/eventViewTabs";
import { isEventProjectManager } from "../../../../utils/companyPortal/eventApi";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  canEditCompanyModule,
  canViewCompanyModules,
  isCompanyOwner,
} from "../../../../utils/Permissions";

import "./EventTabs.scss";

export const EVENT_TABS = EVENT_VIEW_TABS;

const EventTabs = ({
  event,
  onRefreshEvent,
  activeTab: activeTabProp,
  onTabChange,
  selectedRound,
  onRoundSelect,
  readOnly = false,
  hideApplicants = false,
  portalVariant = "company-portal",
  additionalTabs,
  renderAdditionalTab,
}) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { events } = messages[lang];
  const t = events.viewEvents;
  const isClientPortal = portalVariant === "client-portal";
  /** Company portal: the Crew Size tab is shown on every event, whatever its status (PROLINE-4088). */
  const showCrewSizeTab = !isClientPortal;
  const isReadOnly = readOnly || isClientPortal;
  /** Shortcuts into the edit wizard; hidden from employees who may only view events. */
  const canOpenEditWizard =
    !isReadOnly && canEditCompanyModule(companyPermissionModules.events, login);
  const showApplicantsTab = useMemo(() => {
    if (hideApplicants || isClientPortal) return false;
    if (
      portalVariant === "company-portal" &&
      !canViewCompanyModules(companyPermissionModules.joiningEvents, login)
    ) {
      return false;
    }
    return shouldShowEventApplicantsTab(event);
  }, [event, hideApplicants, isClientPortal, login, portalVariant]);

  // Evaluations require the `evaluations` permission and, for RBAC employees,
  // being assigned as a project manager on this event. Owners bypass the PM check.
  // The tab is only visible for ongoing, completed (past) and cancelled events.
  const showEvaluationsTab = useMemo(() => {
    if (isClientPortal) return false;
    if (!shouldShowEventEvaluationsTab(event)) return false;
    if (!canViewCompanyModules(companyPermissionModules.evaluations, login)) {
      return false;
    }
    return isCompanyOwner(login) || isEventProjectManager(event, login?.id);
  }, [isClientPortal, login, event]);

  const showPayrollTab = useMemo(() => {
    if (!isEventPayrollTabEnabled) return false;
    if (isClientPortal) return false;
    if (!shouldShowEventPayrollTab(event)) return false;
    return canViewCompanyModules(companyPermissionModules.payroll, login);
  }, [event, isClientPortal, login]);

  const showTimesheetTab = useMemo(() => {
    if (isClientPortal) return false;
    if (!showApplicantsTab) return false;
    return canViewCompanyModules(companyPermissionModules.timesheet, login);
  }, [isClientPortal, login, showApplicantsTab]);

  const showReportsTab = useMemo(() => {
    if (isClientPortal) return false;
    return canViewCompanyModules(companyPermissionModules.reports, login);
  }, [isClientPortal, login]);

  // Client Reports is a shared file-exchange tab: the client always sees it,
  // company employees need the Manage Reports module.
  const showClientReportsTab = useMemo(() => {
    if (isClientPortal) return true;
    return canViewCompanyModules(companyPermissionModules.manageReports, login);
  }, [isClientPortal, login]);

  const showHeatMapTab = useMemo(() => {
    if (isClientPortal) return true;
    return canViewCompanyModules(companyPermissionModules.heatmap, login);
  }, [isClientPortal, login]);

  // Controlled when onTabChange is provided (company portal), uncontrolled otherwise.
  const isTabControlled = onTabChange !== undefined;

  const allowedTabValues = useMemo(
    () => [
      ...getAllowedEventViewTabs(
        showApplicantsTab,
        showEvaluationsTab,
        false,
        showTimesheetTab,
        showReportsTab,
        showPayrollTab,
        showHeatMapTab,
        showClientReportsTab,
      ),
      ...additionalTabs.map((tab) => tab.value),
      ...(showCrewSizeTab ? [EVENT_VIEW_TABS.crewSize] : []),
    ],
    [
      additionalTabs,
      showCrewSizeTab,
      showApplicantsTab,
      showEvaluationsTab,
      showTimesheetTab,
      showReportsTab,
      showPayrollTab,
      showHeatMapTab,
      showClientReportsTab,
    ],
  );

  const activeTabFromUrl = useMemo(
    () =>
      resolveEventViewTabFromSearch(location.search, allowedTabValues) ??
      EVENT_VIEW_DEFAULT_TAB,
    [allowedTabValues, location.search],
  );

  const [hideTabNav, setHideTabNav] = useState(false);

  const activeTab = isTabControlled ? activeTabProp : activeTabFromUrl;

  useEffect(() => {
    if (activeTab !== EVENT_VIEW_TABS.payroll) {
      setHideTabNav(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isTabControlled) return;
    if (activeTab === EVENT_VIEW_TABS.timesheet && !showTimesheetTab) {
      onTabChange(EVENT_VIEW_DEFAULT_TAB);
      return;
    }
    if (activeTab === EVENT_VIEW_TABS.reports && !showReportsTab) {
      onTabChange(EVENT_VIEW_DEFAULT_TAB);
      return;
    }
    if (activeTab === EVENT_VIEW_TABS.clientReports && !showClientReportsTab) {
      onTabChange(EVENT_VIEW_DEFAULT_TAB);
    }
  }, [
    activeTab,
    isTabControlled,
    onTabChange,
    showTimesheetTab,
    showReportsTab,
    showClientReportsTab,
  ]);

  useEffect(() => {
    if (isTabControlled) return;

    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    if (tabFromUrl && allowedTabValues.includes(tabFromUrl)) {
      return;
    }

    // Keep deep-link query params until the event is loaded and we know which
    // tabs are allowed. Store starts as `event: {}`, so check `id`.
    if (!event?.id && tabFromUrl) {
      return;
    }

    const params = new URLSearchParams(location.search);
    params.set("tab", EVENT_VIEW_DEFAULT_TAB);
    params.delete(TIMESHEET_URL_PARAMS.applicant);
    params.delete(PAYROLL_URL_PARAMS.generate);
    params.delete(ZONE_APPLICANTS_URL_PARAMS.zone);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [
    allowedTabValues,
    event,
    history,
    isTabControlled,
    location.pathname,
    location.search,
  ]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { value: EVENT_VIEW_TABS.basicInfo, label: t.basicInfo },
      ...(showCrewSizeTab
        ? [{ value: EVENT_VIEW_TABS.crewSize, label: t.crewSizeTab.label }]
        : []),
      { value: EVENT_VIEW_TABS.roles, label: t.roles },
      { value: EVENT_VIEW_TABS.zones, label: t.zones },
      {
        value: EVENT_VIEW_TABS.requirementsAndComments,
        label: t.requirementsAndComments,
      },
    ];

    if (showApplicantsTab) {
      baseTabs.push({ value: EVENT_VIEW_TABS.applicants, label: t.applicants });
    }

    if (showPayrollTab) {
      baseTabs.push({ value: EVENT_VIEW_TABS.payroll, label: t.payroll });
    }

    if (showEvaluationsTab) {
      baseTabs.push({ value: EVENT_TABS.evaluations, label: t.evaluations });
    }

    // Portal-supplied tabs (e.g. the client satisfaction survey) take the
    // evaluations slot so client portal order matches the company portal.
    baseTabs.push(...additionalTabs);

    if (showTimesheetTab) {
      baseTabs.push({ value: EVENT_TABS.timesheet, label: t.timesheet });
    }

    if (showReportsTab) {
      baseTabs.push({ value: EVENT_TABS.reports, label: t.reports });
    }

    if (showHeatMapTab) {
      baseTabs.push({ value: EVENT_TABS.heatMap, label: t.heatMap });
    }

    if (showClientReportsTab) {
      baseTabs.push({
        value: EVENT_TABS.clientReports,
        label: t.clientReports,
      });
    }

    const next = [...baseTabs];
    const urlTab = resolveEventViewTabFromSearch(
      location.search,
      allowedTabValues,
    );
    if (
      urlTab &&
      !next.some((tab) => tab.value === urlTab) &&
      (allowedTabValues.includes(urlTab) ||
        (!event?.id && isKnownEventViewTab(urlTab)))
    ) {
      const labelByValue = {
        [EVENT_VIEW_TABS.basicInfo]: t.basicInfo,
        [EVENT_VIEW_TABS.roles]: t.roles,
        [EVENT_VIEW_TABS.zones]: t.zones,
        [EVENT_VIEW_TABS.requirementsAndComments]: t.requirementsAndComments,
        [EVENT_VIEW_TABS.applicants]: t.applicants,
        [EVENT_VIEW_TABS.payroll]: t.payroll,
        [EVENT_VIEW_TABS.evaluations]: t.evaluations,
        [EVENT_VIEW_TABS.timesheet]: t.timesheet,
        [EVENT_VIEW_TABS.reports]: t.reports,
        [EVENT_VIEW_TABS.clientReports]: t.clientReports,
        [EVENT_VIEW_TABS.heatMap]: t.heatMap,
      };
      next.push({ value: urlTab, label: labelByValue[urlTab] });
    }

    return next;
  }, [
    additionalTabs,
    allowedTabValues,
    event?.id,
    location.search,
    showApplicantsTab,
    showEvaluationsTab,
    showPayrollTab,
    showTimesheetTab,
    showReportsTab,
    showClientReportsTab,
    showHeatMapTab,
    t.applicants,
    t.basicInfo,
    t.evaluations,
    t.reports,
    t.clientReports,
    t.payroll,
    t.heatMap,
    t.requirementsAndComments,
    t.roles,
    t.timesheet,
    t.zones,
  ]);

  const navigateToZones = () => {
    if (isReadOnly || !event?.id) return;

    history.push({
      pathname: ROUTE_PATHS.companyEditEvent.replace(":id", event.id),
      state: {
        initialStep: getCompanyEditStep(event, EVENT_FORM_STEP_IDS.zonesRequirements),
      },
    });
  };

  /** Resumes the draft in the edit wizard, on its first incomplete step (Publish & Pay once all are done). */
  const navigateToRoles = () => {
    if (isReadOnly || !event?.id) return;

    history.push({
      pathname: ROUTE_PATHS.companyEditEvent.replace(":id", event.id),
      state: {
        initialStep: getCompanyEditStep(event, EVENT_FORM_STEP_IDS.rolesShifts),
      },
    });
  };

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) {
      return;
    }

    if (isTabControlled) {
      onTabChange(nextTab);
    }

    const params = new URLSearchParams(location.search);
    params.set("tab", nextTab);
    if (nextTab !== EVENT_VIEW_TABS.timesheet) {
      params.delete(TIMESHEET_URL_PARAMS.applicant);
    }
    if (nextTab !== EVENT_VIEW_TABS.payroll) {
      params.delete(PAYROLL_URL_PARAMS.generate);
    }
    if (nextTab !== EVENT_VIEW_TABS.reports) {
      params.delete(EVENT_REPORT_URL_PARAM);
    }
    if (nextTab !== EVENT_VIEW_TABS.zones) {
      params.delete(ZONE_APPLICANTS_URL_PARAMS.zone);
    }
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
    onRoundSelect?.(null);
  };

  return (
    <UITabs
      tabs={tabs}
      value={activeTab}
      onChange={handleTabChange}
      variant="scrollable"
      scrollButtons={false}
      className={hideTabNav ? "ui-tabs--nav-hidden" : ""}
      classNames={{ root: "event-tabs" }}
      rootSx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-32)",
      }}
    >
      {activeTab === EVENT_VIEW_TABS.basicInfo && (
        <EventBasicInfo
          event={event}
          portalVariant={portalVariant}
        />
      )}
      {activeTab === EVENT_VIEW_TABS.crewSize && showCrewSizeTab && (
        <EventCrewSize
          event={event}
          onChooseCrewSize={
            !isReadOnly &&
            canEditCompanyModule(companyPermissionModules.events, login)
              ? () =>
                  history.push({
                    pathname: ROUTE_PATHS.companyEditEvent.replace(":id", event.id),
                    state: {
                      initialStep: getCompanyEditStep(event, EVENT_FORM_STEP_IDS.crewSize),
                    },
                  })
              : undefined
          }
          canRequestUpgrade={
            !isReadOnly &&
            canEditCompanyModule(companyPermissionModules.events, login)
          }
          onRefreshEvent={onRefreshEvent}
        />
      )}
      {activeTab === EVENT_VIEW_TABS.roles && (
        <EventRoles
          event={event}
          portalVariant={portalVariant}
          onAddRole={canOpenEditWizard ? navigateToRoles : undefined}
          showCapacityBanner={!isClientPortal}
          onRefreshEvent={onRefreshEvent}
          canUpgradeCrewSize={
            !isReadOnly &&
            canEditCompanyModule(companyPermissionModules.events, login)
          }
        />
      )}
      {activeTab === EVENT_VIEW_TABS.zones && (
        <EventZones
          event={event}
          onAddZone={canOpenEditWizard ? navigateToZones : undefined}
          canViewApplicants={!isClientPortal}
          readOnly={isReadOnly}
          portalVariant={ZONE_APPLICANTS_PORTALS.company}
          onRefreshEvent={onRefreshEvent}
        />
      )}
      {activeTab === EVENT_VIEW_TABS.requirementsAndComments && (
        <RequirementsComments event={event} />
      )}
      {activeTab === EVENT_VIEW_TABS.applicants && showApplicantsTab ? (
        <JoiningRequestsApplicants
          event={event}
          onRefreshEvent={onRefreshEvent}
          onNavigateToZones={isReadOnly ? undefined : navigateToZones}
        />
      ) : null}
      {activeTab === EVENT_VIEW_TABS.evaluations && showEvaluationsTab && (
        <EvaluationRounds
          event={event}
          selectedRound={selectedRound}
          onRoundSelect={onRoundSelect}
          portalVariant={portalVariant}
        />
      )}
      {additionalTabs.some((tab) => tab.value === activeTab) &&
        renderAdditionalTab?.(activeTab)}
      {activeTab === EVENT_VIEW_TABS.payroll && showPayrollTab ? (
        <CompanyEventPayrollTab
          event={event}
          onNestedViewChange={setHideTabNav}
          onRefreshEvent={onRefreshEvent}
        />
      ) : null}
      {activeTab === EVENT_VIEW_TABS.timesheet && showTimesheetTab ? (
        <CompanyEventTimesheetTab event={event} />
      ) : null}
      {activeTab === EVENT_VIEW_TABS.reports && showReportsTab ? (
        <CompanyEventReportsTab event={event} />
      ) : null}
      {activeTab === EVENT_VIEW_TABS.clientReports && showClientReportsTab ? (
        isClientPortal ? (
          <ClientEventClientReportsTab event={event} />
        ) : (
          <CompanyEventClientReportsTab event={event} />
        )
      ) : null}
      {activeTab === EVENT_VIEW_TABS.heatMap && showHeatMapTab ? (
        <EventHeatMap event={event} portalVariant={portalVariant} />
      ) : null}
    </UITabs>
  );
};

EventTabs.propTypes = {
  event: PropTypes.object,
  onRefreshEvent: PropTypes.func,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  selectedRound: PropTypes.object,
  onRoundSelect: PropTypes.func,
  readOnly: PropTypes.bool,
  hideApplicants: PropTypes.bool,
  portalVariant: PropTypes.oneOf(["company-portal", "client-portal"]),
  additionalTabs: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ),
  renderAdditionalTab: PropTypes.func,
};

EventTabs.defaultProps = {
  event: null,
  onRefreshEvent: undefined,
  activeTab: undefined,
  onTabChange: undefined,
  selectedRound: null,
  onRoundSelect: undefined,
  readOnly: false,
  hideApplicants: false,
  portalVariant: "company-portal",
  additionalTabs: [],
  renderAdditionalTab: undefined,
};

export default EventTabs;
